/**
 * Companies Management
 * CRUD with type filtering, location associations, detail panel.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  X,
  Check,
  Loader,
  Edit2,
  Trash2,
  ArrowLeft,
  MapPin,
  AlertCircle,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import { TableSkeleton, EmptyState } from '../shared';
import ThemedSelect from '../../../common/ThemedSelect';

/* ── Type Badge ─────────────────────────────── */
const TYPE_LABELS = {
  Hauler: 'Haulier',
  Supplier: 'Supplier',
  Customer: 'Customer',
  Internal: 'Internal',
};

const TypeBadge = ({ type }) => {
  const cfg = {
    Hauler: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
    Supplier: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
    Customer: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    Internal: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  };
  const c = cfg[type] || cfg.Internal;
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full border ${c.bg} ${c.text} ${c.border}`}
    >
      {TYPE_LABELS[type] || type}
    </span>
  );
};

/* ── Helper: map PascalCase DB row to form fields (for editing) ── */
const dbToForm = (co) => ({
  company_code: co.company_code || '',
  company_name: co.company_name || '',
  company_type: co.company_type || 'Customer',
  tax_identification: co.tax_identification || '',
  business_registration: co.business_registration || '',
  primary_contact_name: co.primary_contact_name || '',
  primary_contact_email: co.primary_contact_email || '',
  primary_contact_phone: co.primary_contact_phone || '',
  address: co.address || '',
  city: co.city || '',
  state: co.state_province || '',
  country: co.country || '',
  postal_code: co.postal_code || '',
  active_status: co.active_status != null ? co.active_status : true,
});

const emptyForm = () => ({
  company_code: '',
  company_name: '',
  company_type: 'Customer',
  tax_identification: '',
  business_registration: '',
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  address: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  active_status: true,
});

/* ── Company Modal ──────────────────────────── */
const CompanyModal = ({ company, onSave, onCancel }) => {
  const [form, setForm] = useState(company ? dbToForm(company) : emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const types = [
    { value: 'Hauler', label: 'Haulier' },
    { value: 'Supplier', label: 'Supplier' },
    { value: 'Customer', label: 'Customer' },
    { value: 'Internal', label: 'Internal' },
  ];

  const set = (f, v) => {
    setForm((p) => ({ ...p, [f]: v }));
    setErrors((p) => ({ ...p, [f]: '' }));
  };

  const handleSubmit = async () => {
    const e = {};
    if (!form.company_code) e.company_code = 'Required';
    if (!form.company_name) e.company_name = 'Required';
    if (!form.company_type) e.company_type = 'Required';
    setErrors(e);
    if (Object.keys(e).length) return;
    try {
      setSaving(true);
      company
        ? await orderService.updateCompany(company.company_id, form)
        : await orderService.createCompany(form);
      onSave();
    } catch (err) {
      console.error(err);
      alert('Failed to save company.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (label, field, placeholder, { required, type = 'text', half } = {}) => (
    <div className={half ? '' : 'col-span-full'}>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={type}
        value={form[field]}
        onChange={(e) => set(field, e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2.5 border rounded-lg text-sm transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none ${errors[field] ? 'border-red-400' : 'border-slate-200'}`}
      />
      {errors[field] && <p className="text-red-500 text-xs mt-1 font-medium">{errors[field]}</p>}
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-purple-600" />
            </div>
            <h3 className="text-base font-bold text-slate-800">
              {company ? 'Edit Company' : 'New Company'}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            {renderField('Company Code', 'company_code', 'e.g. COM-001', {
              required: true,
              half: true,
            })}
            {renderField('Company Name', 'company_name', 'e.g. Acme Corp', {
              required: true,
              half: true,
            })}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Company Type <span className="text-red-400">*</span>
              </label>
              <ThemedSelect
                value={form.company_type}
                onChange={(e) => set('company_type', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm bg-surface transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none ${errors.company_type ? 'border-red-400' : 'border-slate-200'}`}
              >
                <option value="">Select Type</option>
                {types.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </ThemedSelect>
              {errors.company_type && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.company_type}</p>
              )}
            </div>
            {renderField('Tax ID', 'tax_identification', 'e.g. 12-3456789', { half: true })}
          </div>

          {/* Contact */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Contact Information
            </p>
            <div className="space-y-3">
              {renderField('Contact Name', 'primary_contact_name', 'Full name')}
              <div className="grid grid-cols-2 gap-4">
                {renderField('Email', 'primary_contact_email', 'email@company.com', {
                  type: 'email',
                  half: true,
                })}
                {renderField('Phone', 'primary_contact_phone', '+1 234 567 8900', {
                  type: 'tel',
                  half: true,
                })}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Address
            </p>
            <div className="space-y-3">
              {renderField('Street Address', 'address', '123 Main Street')}
              <div className="grid grid-cols-3 gap-3">
                {renderField('City', 'city', 'City', { half: true })}
                {renderField('State/Province', 'state', 'State/Province', { half: true })}
                {renderField('Country', 'country', 'Country', { half: true })}
              </div>
              <div className="max-w-[200px]">
                {renderField('Postal Code', 'postal_code', '12345')}
              </div>
            </div>
          </div>

          {/* Active */}
          <div className="flex items-center justify-between py-2 border-t border-slate-100">
            <div>
              <p className="text-sm font-semibold text-slate-700">Active Company</p>
              <p className="text-xs text-slate-400">
                Inactive companies won't appear in order forms
              </p>
            </div>
            <button
              type="button"
              onClick={() => set('active_status', !form.active_status)}
              className={`relative w-10 h-5.5 rounded-full transition-colors ${form.active_status ? 'bg-emerald-500' : 'bg-slate-300'}`}
              style={{ width: 40, height: 22 }}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] bg-surface rounded-full shadow transition-transform ${form.active_status ? 'translate-x-[18px]' : ''}`}
              />
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button
            onClick={onCancel}
            className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40 transition-colors text-sm font-semibold flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {company ? 'Update' : 'Create'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Location Association Modal ─────────────── */
const LocationAssocModal = ({ company, locations, onSave, onCancel }) => {
  const [locId, setLocId] = useState('');
  const [assocType, setAssocType] = useState('Both');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!locId) return;
    try {
      setSaving(true);
      await orderService.associateCompanyLocation(company.company_id, locId, assocType);
      onSave();
    } catch (err) {
      console.error(err);
      alert('Failed to associate location.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-surface rounded-xl shadow-2xl max-w-md w-full animate-slideUp"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-teal-600" />
            <h3 className="text-sm font-bold text-slate-800">Associate Location</h3>
          </div>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Location
            </label>
            <ThemedSelect
              value={locId}
              onChange={(e) => setLocId(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-surface focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            >
              <option value="">Choose a location...</option>
              {locations.map((l) => (
                <option key={l.location_id} value={l.location_id}>
                  {l.location_code} — {l.location_name}
                </option>
              ))}
            </ThemedSelect>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Association Type
            </label>
            <div className="flex gap-3">
              {['Dispatch', 'Receipt', 'Both'].map((t) => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="assocType"
                    value={t}
                    checked={assocType === t}
                    onChange={(e) => setAssocType(e.target.value)}
                    className="w-4 h-4 text-teal-600 border-slate-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-700">{t}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button
            onClick={onCancel}
            className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !locId}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40 transition-colors text-sm font-semibold"
          >
            {saving ? 'Associating...' : 'Associate'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Confirm Dialog ─────────────────────────── */
const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div
    className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    onClick={onCancel}
  >
    <div
      className="bg-surface rounded-xl shadow-2xl max-w-sm w-full p-5 animate-slideUp"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-4 h-4 text-amber-500" />
        </div>
        <p className="text-sm text-slate-700 font-medium">{message}</p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════ */
const Companies = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(null);
  const [companyLocs, setCompanyLocs] = useState([]);
  const [showLocModal, setShowLocModal] = useState(false);
  const [allLocs, setAllLocs] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await orderService.getCompanies();
      setCompanies(d.companies || []);
      setFiltered(d.companies || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLocs = useCallback(async (id) => {
    try {
      const d = await orderService.getCompanyLocations(id);
      setCompanyLocs(d.locations || d || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAllLocs = useCallback(async () => {
    try {
      const d = await orderService.getLocations({ active: true });
      setAllLocs(d.locations || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    load();
    loadAllLocs();
  }, [load, loadAllLocs]);

  useEffect(() => {
    let f = companies;
    if (search) {
      const s = search.toLowerCase();
      f = f.filter(
        (c) =>
          c.company_code?.toLowerCase().includes(s) || c.company_name?.toLowerCase().includes(s)
      );
    }
    if (typeFilter) f = f.filter((c) => c.company_type === typeFilter);
    setFiltered(f);
  }, [companies, search, typeFilter]);

  const selectCompany = (co) => {
    setSelected(co);
    loadLocs(co.company_id);
  };

  const handleDelete = async (co) => {
    try {
      await orderService.deleteCompany(co.company_id);
      if (selected?.company_id === co.company_id) {
        setSelected(null);
        setCompanyLocs([]);
      }
      load();
    } catch {
      alert('Failed. Company may be in use.');
    }
    setDeleting(null);
  };

  const handleToggleActive = (co) => {
    const payload = dbToForm(co);
    payload.active_status = !co.active_status;
    orderService.updateCompany(co.company_id, payload).then(load);
  };

  const typeColors = [
    { value: '', label: 'All' },
    { value: 'Hauler', label: 'Haulier' },
    { value: 'Supplier', label: 'Supplier' },
    { value: 'Customer', label: 'Customer' },
    { value: 'Internal', label: 'Internal' },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Back nav */}
      <button
        onClick={() => navigate('/settings/modules/order-management/master-data')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-5"
      >
        <ArrowLeft className="w-4 h-4" />
        Master Data
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center shadow-sm">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">Companies</h1>
              <span
                className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              >
                {filtered.length}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              Hauliers, suppliers, customers, and internal entities
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
          className="px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center text-sm font-semibold shadow-sm gap-2"
        >
          <Plus className="w-4 h-4" /> Add Company
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-slate-200 p-3 mb-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Type pills */}
          <div className="flex flex-wrap gap-1.5">
            {typeColors.map((t) => (
              <button
                key={t.value}
                onClick={() => setTypeFilter(t.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  typeFilter === t.value
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or name..."
              className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table + Detail Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Table */}
        <div className={selected ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <TableSkeleton rows={6} cols={6} />
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={Building2}
                title="No companies found"
                description={
                  search || typeFilter ? 'Adjust your filters' : 'Add your first company'
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/60">
                      {['Code', 'Name', 'Type', 'Contact', 'Status', ''].map((h) => (
                        <th
                          key={h}
                          className={`px-4 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-widest ${h === '' ? 'text-right' : 'text-left'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((co, i) => (
                      <tr
                        key={co.company_id}
                        onClick={() => selectCompany(co)}
                        className={`cursor-pointer transition-colors animate-slideUp ${
                          selected?.company_id === co.company_id
                            ? 'bg-teal-50/40 border-l-2 border-l-teal-500'
                            : 'hover:bg-slate-50'
                        } ${!co.active_status ? 'opacity-50' : ''}`}
                        style={{ animationDelay: `${i * 25}ms` }}
                      >
                        <td className="px-4 py-3">
                          <span
                            className="text-sm font-bold text-slate-800"
                            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                          >
                            {co.company_code}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-800 font-medium">
                            {co.company_name}
                          </div>
                          {co.city && <div className="text-xs text-slate-400">{co.city}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <TypeBadge type={co.company_type} />
                        </td>
                        <td className="px-4 py-3">
                          {co.primary_contact_email && (
                            <div className="text-xs text-slate-600">{co.primary_contact_email}</div>
                          )}
                          {co.primary_contact_phone && (
                            <div className="text-[10px] text-slate-400">
                              {co.primary_contact_phone}
                            </div>
                          )}
                          {!co.primary_contact_email && !co.primary_contact_phone && (
                            <span className="text-xs text-slate-300">&mdash;</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleActive(co);
                            }}
                            className={`relative inline-flex items-center rounded-full transition-colors ${co.active_status ? 'bg-emerald-500' : 'bg-slate-300'}`}
                            style={{ width: 36, height: 20 }}
                            title={co.active_status ? 'Active' : 'Inactive'}
                          >
                            <span
                              className={`inline-block w-4 h-4 rounded-full bg-surface shadow transition-transform ${co.active_status ? 'translate-x-[18px]' : 'translate-x-[2px]'}`}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditing(co);
                                setShowModal(true);
                              }}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleting(co);
                              }}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Detail Panel */}
        {selected && (
          <div className="lg:col-span-1">
            <div className="bg-surface rounded-xl border border-slate-200 sticky top-6 overflow-hidden">
              {/* Panel header */}
              <div className="bg-slate-50 px-5 py-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-800">{selected.company_name}</h3>
                  <button
                    onClick={() => {
                      setSelected(null);
                      setCompanyLocs([]);
                    }}
                    className="p-1 rounded hover:bg-slate-200 text-slate-400"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-1">
                  <TypeBadge type={selected.company_type} />
                </div>
              </div>

              <div className="p-5 space-y-3">
                {/* Details */}
                <div className="space-y-2.5 text-sm">
                  {selected.tax_identification && (
                    <div>
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Tax ID
                      </span>
                      <p
                        className="font-medium text-slate-700"
                        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      >
                        {selected.tax_identification}
                      </p>
                    </div>
                  )}
                  {selected.primary_contact_name && (
                    <div>
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Contact
                      </span>
                      <p className="font-medium text-slate-700">{selected.primary_contact_name}</p>
                      {selected.primary_contact_email && (
                        <p className="text-xs text-slate-500">{selected.primary_contact_email}</p>
                      )}
                      {selected.primary_contact_phone && (
                        <p className="text-xs text-slate-400">{selected.primary_contact_phone}</p>
                      )}
                    </div>
                  )}
                  {(selected.city || selected.address) && (
                    <div>
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Address
                      </span>
                      {selected.address && (
                        <p className="text-xs text-slate-600">{selected.address}</p>
                      )}
                      <p className="text-xs text-slate-500">
                        {[selected.city, selected.state_province, selected.country]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Locations */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Locations
                    </h4>
                    <button
                      onClick={() => setShowLocModal(true)}
                      className="p-1 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {companyLocs.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3">
                      No locations associated
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {companyLocs.map((a) => (
                        <div
                          key={a.location_id}
                          className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg group"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-slate-700 truncate">
                                {a.location_name}
                              </p>
                              <p className="text-[10px] text-slate-400">{a.association_type}</p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              orderService
                                .removeCompanyLocationAssociation(
                                  selected.company_id,
                                  a.location_id
                                )
                                .then(() => loadLocs(selected.company_id))
                            }
                            className="p-1 rounded text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="pt-3 border-t border-slate-100 flex gap-2">
                  <button
                    onClick={() => {
                      setEditing(selected);
                      setShowModal(true);
                    }}
                    className="flex-1 px-3 py-2 text-xs font-semibold text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => setDeleting(selected)}
                    className="px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <CompanyModal
          company={editing}
          onSave={() => {
            setShowModal(false);
            setEditing(null);
            load();
          }}
          onCancel={() => {
            setShowModal(false);
            setEditing(null);
          }}
        />
      )}
      {showLocModal && selected && (
        <LocationAssocModal
          company={selected}
          locations={allLocs}
          onSave={() => {
            setShowLocModal(false);
            loadLocs(selected.company_id);
          }}
          onCancel={() => setShowLocModal(false)}
        />
      )}
      {deleting && (
        <ConfirmDialog
          message={`Delete "${deleting.company_name}"? This cannot be undone.`}
          onConfirm={() => handleDelete(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
};

export default Companies;
